import { useCallback } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';
import { getFragmentData } from '@e-socna/data-layer';
import { ensureDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { useFindAllResolvableServicesQuery } from '../../../components/ResolvableServiceList/hooks/useFindAllResolvableServicesQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ServiceUsageCardCmp_ResolvableServiceBaseInterface_Fragment } from '../../../components/ServiceUsages/Card/fragments/resolvable-service.fragment';
import { getServiceCode } from '../../../components/ServiceUsages/Card/utils/get-service-code.util';
import { N_A } from '../../../constants/labels.constant';
import { checkIfIsId } from '../../../utils/check-if-is-id.util';
import type { ServiceUsageCardCmp_ResolvableServiceBaseInterface } from '../../../components/ServiceUsages/Card/fragments/resolvable-service.fragment';
import type { ServiceUsageCardCmp_ResolvableServiceUsage } from '../../../components/ServiceUsages/Card/fragments/resolvable-service-usage.fragment';

import { ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment } from '../../ResolvableService/fragments/resolvable-service-base-interface.fragment';
import type { ResolvableServiceForm_ResolvableServiceBaseInterfaceData } from '../../ResolvableService/fragments/resolvable-service-base-interface.fragment';
import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../../ResolvableServiceUsage/fragments/resolvable-service-usage.fragment';

interface Params {
  readonly fallbackResolvableServiceUsages?:
    readonly ServiceUsageCardCmp_ResolvableServiceUsage[] | null | undefined;
}

// Resolves a human-readable label for a resolvable service usage, whichever
// its content origin: a catalog-origin usage is labelled from the fallback
// (catalog) usage it points to, a modified/special one from its own service.
export const useResolvableServiceUsageLabel = ({
  fallbackResolvableServiceUsages,
}: Readonly<Params>) => {
  const { contentOriginsCodeMap } = useReferencesContext();

  const { data, isLoading } = useFindAllResolvableServicesQuery();

  const getLabel = useCallback(
    (
      resolvableServiceUsage: Readonly<ResolvableServiceUsageForm_ResolvableServiceUsage>,
    ) => {
      const extractFromService = (
        pServiceOrId?:
          | Readonly<ResolvableServiceForm_ResolvableServiceBaseInterfaceData>
          | Readonly<ServiceUsageCardCmp_ResolvableServiceBaseInterface>
          | Id
          | null,
      ) => {
        const { serviceOrId } = ensureDefined({ serviceOrId: pServiceOrId });

        if (checkIfIsId(serviceOrId)) {
          const allResolvableServices =
            data?.findAllResolvableServices ?? EMPTY_ARRAY;

          const resolvedService = allResolvableServices.find(
            (service) => service._id === resolvableServiceUsage.serviceId,
          );

          return resolvedService
            ? `${resolvedService.code} - ${resolvedService.label}`
            : N_A;
        }

        return `${getServiceCode(serviceOrId)} - ${serviceOrId.label}`;
      };

      if (
        resolvableServiceUsage.contentOriginId ===
        contentOriginsCodeMap.Catalog._id
      ) {
        const { serviceUsageOrigin } = ensureDefined({
          serviceUsageOrigin: resolvableServiceUsage.serviceUsageOrigin,
        });

        const { origin } = ensureDefined({
          origin: fallbackResolvableServiceUsages?.find(
            (usage) => usage._id === serviceUsageOrigin.serviceUsageId,
          ),
        });

        const specialService = getFragmentData(
          ServiceUsageCardCmp_ResolvableServiceBaseInterface_Fragment,
          origin.specialService,
        );

        return extractFromService(specialService ?? origin.serviceId);
      }

      const specialService = getFragmentData(
        ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment,
        resolvableServiceUsage.specialService,
      );

      return extractFromService(
        specialService ?? resolvableServiceUsage.serviceId,
      );
    },
    [
      contentOriginsCodeMap.Catalog._id,
      data?.findAllResolvableServices,
      fallbackResolvableServiceUsages,
    ],
  );

  return { getLabel, isLoading };
};
